// Top Scorers — League / Club — ranked horizontal bar chart (Phase 3).
//
// The first consumer of the ranked-bar type (CLAUDE.md "Module type registry").
// A thin wrapper: it resolves the data (shared shaping over the `legacyData`
// accessor) and each row's presentation, then hands them to <RankedBars>, which
// owns the React-container / D3-rows mechanics. League ranks the whole division
// (blue bars, fixture teams highlighted); Club ranks one team's scorers (bar
// tinted the club colour, zero-goal players dropped). Both titles share this
// component — the bridge passes a `variant`.

import { clubFromName, shapeLeagueScorers, shapeClubScorers, type ScorerVenue, type ScorerTimeline, type TopScorersLeagueData, type TopScorersClubData } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import RankedBars, { type RankedBarRow } from "./RankedBars.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface TopScorersProps {
  /** League (whole division) vs Club (one team's scorers). */
  variant: "league" | "club";
  fixture: string;
  /** Club only: selected team (full name); falls back to the fixture's home side. */
  team?: string | null;
  /** Venue toggle: "All" | "Home" | "Away". */
  ha?: string | null;
  /** Timeline toggle: "Season" | "5" | "10". */
  timeline?: string | null;
}

export default function TopScorers({
  variant,
  fixture,
  team,
  ha,
  timeline,
}: TopScorersProps) {
  // League and Club are distinct endpoints; the bridge picks the variant.
  const key = variant === "league" ? "top-scorers-league" : "top-scorers-club";
  const { data, isPending, isError, refetch } = useWidgetData(
    key,
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const venue: ScorerVenue =
    ha === "Home" ? "Home" : ha === "Away" ? "Away" : "All";
  const tl: ScorerTimeline =
    timeline === "5" ? "5" : timeline === "10" ? "10" : "Season";

  let rows: RankedBarRow[];
  let barFillClass: string;
  let max: number;

  if (variant === "league") {
    // `key` is "top-scorers-league" in this branch, so `data` is the league
    // payload — assert it, since the hook can't correlate the key with `variant`.
    const shape = shapeLeagueScorers(
      data as TopScorersLeagueData,
      venue,
      tl,
      [t1, t2].filter(Boolean)
    );
    const shaped = shape.rows;
    max = shape.max;
    // Blue bars (colour from the CSS class, so no inline bg); fixture teams
    // highlighted with a white label + count.
    barFillClass = "bar-fill blue bento-fill";
    rows = shaped.map((r) => ({
      name: r.name,
      value: r.goals,
      selected: r.highlighted,
      labelColor: r.highlighted ? "#fff" : "",
      countColor: r.highlighted ? "#fff" : "#888",
    }));
  } else {
    const selected = team || t1;
    const shape = shapeClubScorers(
      (data as TopScorersClubData)[selected] ?? [],
      venue,
      tl
    );
    const shaped = shape.rows;
    max = shape.max;
    const teamColour = clubFromName(selected)?.colour ?? "#2ecc71";
    barFillClass = "bar-fill team bento-fill";
    rows = shaped.map((r) => ({
      name: r.name,
      value: r.goals,
      selected: false,
      bg: teamColour,
      labelColor: "",
      countColor: "",
    }));
  }

  return <RankedBars rows={rows} max={max} barFillClass={barFillClass} />;
}
